import { observer } from "mobx-react";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { TableView } from "../../custom-components/table";
import { FormulaElement } from "../../models/models";
import { useStore } from "../../stores/store";
import { getSortedData } from "./configure-formula";
import { getDetailedResult } from "./module-result-view";

export const sortResults = (
  results: {
    [key: string]: string | number | null;
  }[],
  orderedFormulaElements: FormulaElement[]
) => {
  const orderedGroups = [...new Set(orderedFormulaElements.map((formula) => formula.group))];
  const sortedResults = orderedGroups.flatMap((group) => {
    const formulaResults = results.filter((r) =>
      orderedFormulaElements.find((formula) => formula.group === group && formula.name === r["Renewal Steps/Components"])
    );
    return formulaResults;
  });
  return sortedResults;
};

interface ResultViewProps {
  id?: string;
}

const ResultView = observer(function ResultView(props: ResultViewProps) {
  const { store } = useStore();
  const params = useParams();
  const id = props.id || params.id || "";
  const { parent } = params;
  const formulaElements = store.GetFormulasByModule(id ?? "");
  const formulaElementsOrder = (store.GetModuleById(id ?? "0")?.moduleConfig?.formulaElementsOrder ?? []).map((ie) => ie.toString());
  const orderedFormulaElements = formulaElementsOrder.length > 0 ? (getSortedData(formulaElementsOrder, formulaElements) as FormulaElement[]) : formulaElements;
  let formulaElementsGroups = (orderedFormulaElements as FormulaElement[]).filter((fe) => fe?.group !== "");
  const selectedFormulas =
    parent != null ? orderedFormulaElements.filter((formula) => formula.formulaConfig?.hideInDetailResults != true) : orderedFormulaElements;
  const results = store.results;
  useEffect(() => {
    if (parent == null) {
      if (results.length > 0) store.SetResults([]);
      if (store.resultErrors.length > 0) store.SetErrors([]);
      if (Object.keys(store.errorMessages).length > 0) store.SetErrorMessages({});
    }
  }, [id]);

  let data = selectedFormulas.map((s) => {
    const row: { [key: string]: string | null | number } = {};
    Object.entries(s).forEach(([key, value]) => {
      row[key] = value.toString();
    });
    const pageNo = s.formulaConfig?.expressions?.[0]?.pageNo;
    row["pageNo"] = pageNo !== undefined && pageNo !== null && pageNo !== "" && pageNo !== "0" ? pageNo : "";
    return row;
  });
  let columns = [
    { key: "name", label: "Renewal Steps/Components", width: "40%" },
    { key: "pageNo", label: "Reference" },
    { key: "", label: "Quantity" },
    { key: "", label: "Unit Rate" },
    { key: "", label: "Cost" },
  ];
  if (formulaElementsGroups.length > 0) {
    columns = [{ key: "group", label: "Group" }, ...columns];
  }
  if (store.resultErrors.length > 0) {
    columns = [
      { key: "name", label: "Renewal Steps/Components" },
      { key: "condition", label: "Error Condition" },
      { key: "type", label: "Error Type" },
      { key: "description", label: "Error Description" },
    ];
    formulaElementsGroups = formulaElementsGroups.filter((fg) => store.resultErrors.map((error) => error.formulaId).includes(fg.id.toString()));
    if (formulaElementsGroups.length > 0) {
      columns = [{ key: "group", label: "Group" }, ...columns];
    }
    data = store.resultErrors.map((error) => {
      const row: { [key: string]: string | number | null } = {};
      const formulaElement = formulaElements.find((formula) => formula.id.toString() == error.formulaId);
      columns.forEach((column) => {
        switch (column.key) {
          case "name":
            row[column.key] = error.type !== "Input Error" ? formulaElement?.name ?? "" : error.formulaId;
            break;
          case "condition": {
            const expression = formulaElement?.formulaConfig?.expressions?.find((expression) => expression.id === error.conditionId);
            row[column.key] =
              error.type === "Condition"
                ? expression?.condition ?? ""
                : error.type === "Unit Rate" || error.type === "Look Up"
                ? expression?.unitRate ?? ""
                : expression?.quantity ?? "";
            break;
          }
          case "description":
            row[column.key] = error.description;
            break;
          case "type":
            row[column.key] = error.type;
            break;
          case "group":
            row[column.key] = formulaElement?.group ?? "";
            break;
        }
      });
      return row;
    });
  } else {
    if (results.length > 0) {
      columns = Object.keys(results[0]).map((key) => ({
        key: key,
        label: key,
        dataAlign: "right",
        width: key === "Renewal Steps/Components" ? "40%" : "",
      }));
      // pageNo column - Add
      if (!columns.some((col) => col.key === "pageNo")) {
        columns.splice(1, 0, { key: "pageNo", label: "Reference", width: "" });
      }
      const orderedData = formulaElementsOrder.length > 0 ? sortResults(results, orderedFormulaElements) : results;
      const detailedResult = parent != null ? getDetailedResult(orderedData, selectedFormulas) : orderedData;
      if (formulaElementsGroups.length > 0) {
        columns.splice(0, 0, { key: "group", label: "Group", width: "" });
      }
      data = detailedResult.map((d) => {
        const row: { [key: string]: string | number | null } = {};
        columns.forEach((h) => {
          if (h.key === "group") {
            const formulaElementGroup = formulaElements.find((fe) => fe.name === d["Renewal Steps/Components"])?.group ?? "fixedRow";
            row[h.key] = formulaElementGroup;
          } else if (h.key === "pageNo") {
            const formulaElement = formulaElements.find((fe) => fe.name === d["Renewal Steps/Components"]);
            const pageNo = formulaElement?.formulaConfig?.expressions?.[0]?.pageNo ?? "";
            row[h.key] = row["pageNo"] = pageNo !== undefined && pageNo !== null && pageNo !== "" && pageNo !== "0" ? pageNo : "";
          } else {
            row[h.key] =
              typeof d[h.key] == "number" ? d[h.key]?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "" : d[h.key];
          }
        });
        return row;
      });
    }
  }
  return formulaElementsGroups.length > 0 ? (
    <TableView data={data} columns={columns} height={parent != null ? "100%" : ""} groupBy={"group"} />
  ) : (
    <TableView data={data} columns={columns} height={parent != null ? "100%" : ""} />
  );
});
export default ResultView;
